// Modal shown when user tries to switch sections with unsaved changes
import { useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogFocus } from '@startus/ui';

interface UnsavedChangesModalProps {
  isOpen: boolean;
  onDiscard: () => void;
  onSave: () => void;
  onClose: () => void;
}

export function UnsavedChangesModal({
  isOpen,
  onDiscard,
  onSave,
  onClose,
}: UnsavedChangesModalProps) {
  const { t } = useTranslation('common');
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const questionId = useId();

  // Escape behaves like the X / backdrop: close and keep editing. Initial focus goes to the
  // first focusable element (the close button), so no choice is pre-selected.
  useDialogFocus({ isOpen, containerRef: dialogRef, onEscape: onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" onClick={onClose} />

      {/* Modal */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={questionId}
        className="relative bg-[#1E1E1E] rounded-xl shadow-xl max-w-md w-full mx-4 overflow-hidden"
      >
        {/* Yellow accent bar */}
        <div className="h-full w-2 bg-amber-400 absolute left-0 top-0" />

        {/* Content */}
        <div className="pl-6 pr-4 py-5">
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="absolute top-4 right-4 text-[#F9F9F9] hover:text-white transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Icon + text */}
          <div className="flex gap-4">
            {/* Warning icon */}
            <div className="flex-shrink-0 flex items-center justify-center">
              <svg
                className="w-8 h-8 text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>

            {/* Text */}
            <div className="flex-1 pr-6">
              <p id={titleId} className="text-white font-medium">
                {t('unsavedChanges.title')}
              </p>
              <p id={questionId} className="text-white/60 mt-2">
                {t('unsavedChanges.question')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6 justify-end">
            <button
              type="button"
              onClick={onDiscard}
              className="px-5 py-2.5 text-sm font-medium text-gray-800 bg-[#EAEAEA] rounded-full hover:bg-white/90 transition-colors"
            >
              {t('unsavedChanges.discard')}
            </button>
            <button
              type="button"
              onClick={onSave}
              className="px-5 py-2.5 text-sm font-medium text-white bg-[#19A2F8] hover:bg-[#19A2F8]/80 rounded-full transition-colors"
            >
              {t('unsavedChanges.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
